import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { getPositionStyles, calculateStickyPosition, shouldApplyStickyCenterPosition, } from '../utils/positioning';
import { getAdpxAnimateAttribute, getViewportWidth, hasAdEntranceAnimation, } from '../utils/adAnimation';
import { BREAKPOINTS, useIsMobile } from '../utils/responsive';
export function OfferModal({ isOpen, onClose, settings, styles, children, offerChrome = 'default', }) {
    const modalRef = useRef(null);
    const [stickyPosition, setStickyPosition] = useState(null);
    const [stickyReady, setStickyReady] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(() => getViewportWidth());
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const onResize = () => setViewportWidth(getViewportWidth());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    useEffect(() => {
        if (settings?.embedded?.enabled) {
            return;
        }
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, settings?.embedded?.enabled]);
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen && !settings?.embedded?.enabled) {
                onClose();
            }
        };
        const handleClickOutside = (e) => {
            if (settings?.embedded?.enabled) {
                return;
            }
            if (!settings.close_ad_on_click_outside || !modalRef.current) {
                return;
            }
            // Clicks inside our shadow root retarget `e.target` to the host on
            // document listeners, so `contains(e.target)` is wrong. composedPath
            // includes the real hit targets inside the shadow tree.
            const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
            const clickedInsidePopup = path.length > 0
                ? path.includes(modalRef.current)
                : modalRef.current.contains(e.target);
            if (!clickedInsidePopup) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, settings.close_ad_on_click_outside, settings?.embedded?.enabled]);
    const needsStickyCenter = isOpen &&
        shouldApplyStickyCenterPosition(settings, {
            viewportWidth,
            offerChrome,
            embedded: Boolean(settings?.embedded?.enabled),
        });
    const hasEntranceAnimation = isOpen && hasAdEntranceAnimation(settings, viewportWidth);
    useEffect(() => {
        if (!isOpen) {
            setStickyPosition(null);
            setStickyReady(false);
        }
    }, [isOpen]);
    useEffect(() => {
        if (offerChrome === 'template') {
            setStickyPosition(null);
            setStickyReady(false);
        }
    }, [offerChrome]);
    // Sticky center: snap to integer pixels before the first paint (and before any entrance
    // animation starts). For non-animation modals the popup is hidden until this runs;
    // for animated modals the animation's own opacity:0 from-keyframe covers the first frame.
    useLayoutEffect(() => {
        if (!needsStickyCenter) {
            setStickyReady(true);
            return;
        }
        setStickyReady(false);
        const modal = modalRef.current;
        if (!modal) {
            return;
        }
        const stickyPos = calculateStickyPosition(modal);
        if (stickyPos) {
            setStickyPosition(stickyPos);
            setStickyReady(true);
        }
    }, [needsStickyCenter, viewportWidth, offerChrome]);
    const adpxAnimate = getAdpxAnimateAttribute(settings, viewportWidth);
    if (!isOpen) {
        return null;
    }
    const isMobile = useIsMobile(settings.multi_offer_unit ? BREAKPOINTS.mouMobile : BREAKPOINTS.mobile);
    const isEmbedded = settings.embedded?.enabled;
    const isTemplateChrome = offerChrome === 'template';
    // Embedded mode styles
    if (isEmbedded) {
        return (_jsx("div", { id: 'adpx_modal', ref: modalRef, "data-ms-offer-chrome": offerChrome, style: isTemplateChrome
                ? {
                    width: '100%',
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    fontFamily: 'inherit',
                    overflow: 'visible',
                    boxShadow: 'none',
                    border: 'none',
                    borderTop: 'none',
                }
                : {
                    width: '100%',
                    backgroundColor: styles.popup?.background || '#ffffff',
                    borderRadius: '8px',
                    fontFamily: styles.offerText?.font || 'Roboto, sans-serif',
                    overflow: 'hidden',
                    boxShadow: settings.embedded?.showBorder
                        ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                        : 'none',
                    border: settings.embedded?.showBorder
                        ? '1px solid #ddd'
                        : 'none',
                    borderTop: 'none',
                    // Make this wrapper a containing block so descendant
                    // `position: fixed` overlays (the T&C modal) are scoped + clipped
                    // to the embedded widget instead of escaping to the full viewport.
                    // In normal mode the fixed `:host` already provides this; embedded
                    // `:host` is only `position: relative`, which doesn't contain fixed.
                    transform: 'translateZ(0)',
                }, children: children }));
    }
    // Get positioning styles
    const { overlayStyles, modalStyles, usesMobileVerticalOffset } = getPositionStyles(settings, isMobile);
    const shouldDarkenBg = (settings.darken_bg_non_centered && settings.ad_position !== 'center') ||
        (settings.darken_bg && settings.ad_position === 'center');
    const defaultChromeSizing = {
        minWidth: settings.multi_offer_unit
            ? isMobile
                ? '90%'
                : '950px'
            : isMobile
                ? (styles.popup?.offer_unit_width ?? '90%')
                : '700px',
        maxWidth: settings.multi_offer_unit
            ? isMobile
                ? '90%'
                : '950px'
            : isMobile
                ? (styles.popup?.offer_unit_width ?? '90%')
                : '700px',
        // height: 'max-content',
        overflow: 'hidden',
        backgroundColor: styles.popup?.background || '#ffffff',
        borderRadius: `${styles.popup?.borderRadius?.top_left}px ${styles.popup?.borderRadius?.top_right}px ${styles.popup?.borderRadius?.bottom_right}px ${styles.popup?.borderRadius?.bottom_left}px`,
        fontFamily: styles.offerText?.font || 'Roboto, sans-serif',
        border: settings.embedded?.showBorder && !shouldDarkenBg
            ? '1px solid #ddd'
            : 'none',
        borderTop: 'none',
        boxShadow: `0 10px 15px -3px ${styles.popup?.shadow || 'rgba(0,0,0,0.1)'}, 0 4px 6px -4px ${styles.popup?.shadow || 'rgba(0,0,0,0.1)'}`,
    };
    const templateChromeSizing = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minWidth: 'unset',
        // width: 'max-content',
        maxWidth: isMobile
            ? 'calc(100vw - 24px)'
            : 'min(calc(100vw - 48px), 560px)',
        minHeight: 'min-content',
        height: 'auto',
        overflow: 'visible',
        backgroundColor: 'transparent',
        borderRadius: 0,
        fontFamily: 'inherit',
        border: 'none',
        borderTop: 'none',
        boxShadow: 'none',
    };
    // Base popup styles from SOU.css (template chrome = invisible sizing wrapper only)
    const baseModalStyles = {
        ...modalStyles,
        ...(isTemplateChrome ? templateChromeSizing : defaultChromeSizing),
        position: 'fixed',
        zIndex: settings?.embedded?.enabled ? 0 : 2147483647,
        lineHeight: 'normal',
        userSelect: 'none',
        WebkitUserSelect: 'none',
    };
    // For center positioning, ensure proper centering with both horizontal and vertical
    // Inside Shadow DOM, we need to use absolute positioning relative to shadow root
    if ((settings.ad_position === 'center' || !settings.ad_position) &&
        !usesMobileVerticalOffset) {
        baseModalStyles.left = '50%';
        baseModalStyles.top = '50%';
        baseModalStyles.transform = 'translate(-50%, -50%)';
        baseModalStyles.marginTop = '0';
        baseModalStyles.marginBottom = '0';
    }
    const hideUntilSticky = needsStickyCenter && !stickyReady && !hasEntranceAnimation;
    // Apply sticky position if set (overrides initial center % positioning)
    const finalModalStyles = {
        ...baseModalStyles,
        ...(stickyPosition || {}),
        ...(hideUntilSticky ? { visibility: 'hidden' } : {}),
    };
    // Modal popup mode with positioning
    return (_jsxs(_Fragment, { children: [_jsx("div", { id: 'adpx_modal-overlay', style: overlayStyles }), _jsx("div", { id: 'adpx_popup', className: 'adpx_modal-container adpx_modal-content', ref: modalRef, style: finalModalStyles, "data-ms-offer-chrome": offerChrome, "data-adpx-position": isMobile ? undefined : (settings.ad_position || 'center'), ...(adpxAnimate ? { 'data-adpx-animate': adpxAnimate } : {}), children: children })] }));
}
